import { CallEmsApi, iEmsApiResponseBody, networkFailure } from "../Logic/TenantAPI";

/*
 * DTOs for the TENANT-SCOPED mailbox-health endpoint.
 *
 * Scoped to the mailbox route deliberately. The tenant tier has other routes
 * — email search, email detail, and the two action POSTs — and those belong
 * in their own module (`EmailAPI.ts`) when their sub-projects are built, not
 * here.
 *
 * This is the ordinary tier: the route reads the tenant from the caller's
 * token, so the token selects which tenant's mailboxes come back. That is
 * unlike CoreAPI.ts in this same folder, whose two routes ignore the tenant
 * claim and read across every tenant. Do not infer cross-tenant semantics
 * from the folder name.
 *
 * NULLABILITY IS DIFFERENT HERE, AND DELIBERATELY SO. Every numeric count
 * below is a required `int`, not optional. Contract §2.1: a tenant-scoped
 * read either succeeds or returns an error status, so there is no unmeasured
 * state to represent and no absent-vs-zero question to answer. Do NOT wrap
 * these in `Measured` — that would claim a distinction the wire does not
 * carry.
 *
 * The one field that DOES carry the absent-vs-zero weight is `lastBacklog`.
 * It is genuinely nullable: 0 means "measured, nothing waiting", and absent
 * means "this mailbox did not report a backlog". See mailboxHealth.ts.
 *
 * ON NESTED-OBJECT NULLABILITY, THE SCHEMA CANNOT TELL YOU ANYTHING. An
 * earlier version of this file typed `enablement`, `lastRun`, `counts` and
 * `throughput` all required, reasoning that none of them is marked nullable
 * in the OpenAPI schema. That reasoning was wrong: of the schema's 40
 * nested-object (`$ref`) properties, ZERO are marked nullable, while 86
 * primitive properties are. The generator never marks a `$ref` nullable —
 * "not marked nullable" is true of every nested object whether or not the
 * server can omit it, so it carries no information here at all.
 * `docs/api/mail-processing-contract.md` §4 warns the document is optimistic
 * in places for exactly this reason: generator defaults, not the code.
 *
 * `lastRun` IS proven absent by evidence instead: a live capture of
 * `GET /MailProcessing/Mailboxes` (2026-09-04) returned ten mailboxes, nine
 * of them missing `lastRun` entirely. All nine were `enablement: {inbound:
 * "Disabled", outbound: "Disabled"}` — the field is absent exactly when the
 * mailbox has never run, sensible server behaviour the client's type denied.
 * Typed optional below for that reason.
 *
 * `enablement`, `counts` and `throughput` stay required — also on positive
 * evidence, not on the schema. All ten mailboxes in that same capture carried
 * all three, including the nine fully-disabled, never-run ones: a
 * fully-disabled, never-run mailbox is the most degenerate card the server
 * can emit, and it still reports `enablement`, `counts` (all zero) and
 * `throughput` (zeroed window). If any of the three were ever going to be
 * omitted, that is the card where it would happen, and it was not. The
 * residual risk is that no capture has yet shown a network-level partial
 * response (mid-incident, mid-migration) — if one ever does, that is new
 * evidence and this comment is where to update it, not a reason to guess now.
 */

/** Names a transport, not a health. `Legacy` and `Cloud` both mean enabled. */
export type MailboxDirectionMode = "Disabled" | "Legacy" | "Cloud";

/**
 * Per-side outcome of the last collection run.
 *
 * `LockTimeout` means another collector node held the lock, so this run
 * measured NOTHING. It is not a failure and must never render as one, nor as
 * a zero (contract §2.5).
 *
 * `ConnectFailed` and `Failed` are distinct on purpose: one is reachability,
 * the other is processing. Same kind of information as the Core view's
 * `Failing` versus `Stale`, and collapsing them destroys it.
 */
export type MailboxRunOutcome = "Success" | "Failed" | "LockTimeout" | "ConnectFailed";

export interface MailboxEnablement {
    inbound: MailboxDirectionMode;
    outbound: MailboxDirectionMode;
}

export interface MailboxSideOutcome {
    outcome: MailboxRunOutcome;
    error?: string;
}

export interface MailboxLastRun {
    /** Database-sourced, so OFFSET-LESS. Parse with parseEmsDate, never `new Date()`. */
    startedAt?: string;
    /** Database-sourced, so OFFSET-LESS. Parse with parseEmsDate, never `new Date()`. */
    completedAt?: string;
    receive: MailboxSideOutcome;
    send: MailboxSideOutcome;
}

export interface InboundMailboxCounts {
    failed: number;
    stalled: number;
    deadLettered: number;
}

export interface OutboundMailboxCounts {
    failed: number;
    stalled: number;
    deadLettered: number;
    abandoned: number;
}

export interface MailboxCounts {
    inbound: InboundMailboxCounts;
    outbound: OutboundMailboxCounts;
}

export interface MailboxThroughput {
    /** The server owns the window. Read it for the label; never hardcode "24h". */
    windowHours: number;
    received: number;
    sent: number;
}

export interface MailboxHealthCard {
    mailboxID: number;
    name?: string;
    address?: string;
    enablement: MailboxEnablement;
    /** A collection run is in progress right now. Explains a sibling's LockTimeout. */
    processingNow: boolean;
    /** Server-computed against a server-side threshold. Never re-derive this (contract §2.3). */
    stalled: boolean;
    /**
     * Absent when this mailbox has never run — observed on every disabled
     * mailbox in a live response, never on the one enabled mailbox in the
     * same capture. See the file header for why the schema cannot confirm
     * this either way. See mailboxHealth.ts's `"neverRun"` standing for how
     * an absence is classified on an ENABLED mailbox, where it is unexplained
     * rather than expected.
     */
    lastRun?: MailboxLastRun;
    lastConnectionError?: string;
    /**
     * Mail waiting unread in this mailbox. THE one absent-vs-zero field here.
     *
     * Absent when this mailbox's receive side has not run, so a send-only or
     * newly-added mailbox contributes nothing. This is the per-mailbox source
     * of the Core aggregate's `SumOfKnown` partial-sum problem, and this view
     * is the only place in the feature where the partiality is visible.
     */
    lastBacklog?: number;
    counts: MailboxCounts;
    throughput: MailboxThroughput;
    /** Stamped per request, so this one DOES carry an offset (contract §1.3). */
    asOf: string;
}

/**
 * Every mailbox for ONE tenant — the tenant named by the token.
 *
 * The token must be a tenant-scoped inspection token (see
 * useTenantInspection.ts), not the sys-admin token the Core routes take.
 * Passing the wrong one does not fail loudly: it returns some other tenant's
 * mailboxes.
 *
 * No parameters, no paging. A `200` with `[]` means the tenant has no
 * mailboxes configured, which is an answer and not a failure.
 */
export async function GetMailboxHealth(tenantScopedToken: string): Promise<iEmsApiResponseBody<MailboxHealthCard[]>> {
    try {
        return await CallEmsApi<MailboxHealthCard[]>(tenantScopedToken, "GET", "MailProcessing/Mailboxes");
    } catch {
        return networkFailure<MailboxHealthCard[]>();
    }
}
